import { useCallback } from 'react'
import { InlineEditField } from '@/components/InlineEditField'
import { businessService } from '@/services/businessService'
import { validatePAN, validateGSTIN } from '@/utils/validators'
import { useToast } from '@/components/ui/MobileToast'

interface BasicInfoTabProps {
  business: any
  onUpdate: (updated: any) => void
}

const BUSINESS_TYPE_OPTIONS = [
  { value: 'private_limited', label: 'Private Limited' },
  { value: 'public_limited', label: 'Public Limited' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
  { value: 'llp', label: 'LLP' },
  { value: 'opc', label: 'OPC' },
]

const INDUSTRY_SECTOR_OPTIONS = [
  { value: 'technology', label: 'Technology' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'retail', label: 'Retail' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'finance', label: 'Finance' },
  { value: 'education', label: 'Education' },
  { value: 'construction', label: 'Construction' },
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'services', label: 'Services' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'food_and_beverage', label: 'Food & Beverage' },
  { value: 'textiles', label: 'Textiles' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'software-development', label: 'Software Development' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'other', label: 'Other' },
]

export function BasicInfoTab({ business, onUpdate }: BasicInfoTabProps) {
  const { showToast } = useToast()

  const handleSave = useCallback(
    async (fieldName: string, value: string | number) => {
      try {
        const updated = await businessService.updateBusiness(business.id, {
          [fieldName]: value,
        })
        onUpdate(updated)

        // Check for badge changes in response
        if ((updated as any).badge_awarded) {
          showToast({
            type: 'success',
            title: '🏆 Badge Earned!',
            message: `Congratulations! You earned the "${(updated as any).badge_awarded}" badge.`,
          })
        }
      } catch (err: any) {
        const message =
          err?.response?.data?.error || err?.message || 'Save failed — please try again'
        throw new Error(message)
      }
    },
    [business.id, onUpdate, showToast]
  )

  const panValidator = useCallback((value: string): string | null => {
    if (!value) return null // Allow empty
    const result = validatePAN(value)
    return result.valid ? null : (result.error ?? null)
  }, [])

  const gstinValidator = useCallback((value: string): string | null => {
    if (!value) return null // Allow empty
    const result = validateGSTIN(value)
    return result.valid ? null : (result.error ?? null)
  }, [])

  const isPanVerified = business.pan_verified || business.verification_status === 'verified'
  const isGstinVerified = business.gstin_verified

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Basic Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InlineEditField
            label="Business Name"
            value={business.business_name}
            fieldName="business_name"
            type="text"
            onSave={handleSave}
            placeholder="Enter business name"
          />

          <InlineEditField
            label="Business Type"
            value={business.business_type}
            fieldName="business_type"
            type="select"
            options={BUSINESS_TYPE_OPTIONS}
            onSave={handleSave}
          />

          <InlineEditField
            label="Industry Sector"
            value={business.industry_sector}
            fieldName="industry_sector"
            type="select"
            options={INDUSTRY_SECTOR_OPTIONS}
            onSave={handleSave}
          />

          <InlineEditField
            label="Registered Address"
            value={business.registered_address}
            fieldName="registered_address"
            type="text"
            onSave={handleSave}
            placeholder="Enter registered address"
          />

          <InlineEditField
            label="PAN"
            value={business.pan}
            fieldName="pan"
            type="text"
            validate={panValidator}
            onSave={handleSave}
            disabled={isPanVerified}
            placeholder="ABCDE1234F"
          />

          <InlineEditField
            label="GSTIN"
            value={business.gstin}
            fieldName="gstin"
            type="text"
            validate={gstinValidator}
            onSave={handleSave}
            disabled={isGstinVerified}
            placeholder="22ABCDE1234F1Z5"
          />
        </div>

        {(isPanVerified || isGstinVerified) && (
          <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            Fields marked as verified cannot be edited. Contact support if you need to update them.
          </p>
        )}
      </div>
    </div>
  )
}
